import icsDB from 'libs/DB/icsDB'
import settingSyncService from 'services/settingSync'
import { classInfo, folderEmitter, instanceEmitter } from '../CalendarState'
import { _removeInstances, _createInstances } from '../EventInstanceManager'
import type { ICSSubscription, RefreshInterval } from './types'
import { REFRESH_INTERVALS } from './types'
import {
  _loadSubscriptions,
  _saveSubscriptions,
  _makeCalendarUrl,
  _findCalendar,
  _createAppCalendar,
  _upsertCalendar,
  _removeCalendar,
} from './calendarHelpers'
import { _fetchAndParseICS, _shouldRefresh, _loadCachedICS } from './icsFetcher'
import { _replaceCalendarEvents } from './instanceHelper'
import {
  refreshLocks,
  _reloadLock,
  _setReloadLock,
  _setupTimer,
  _clearTimer,
  clearAllTimers,
  _bindRefreshFn,
} from './timerManager'
import {
  _pushToServer,
  _registerSettingListener,
  resetSyncState,
  _bindServerSyncRefreshFn,
} from './serverSync'

// ──────────────────────────────────────
// Public API
// ──────────────────────────────────────

export type { RefreshInterval, ICSSubscription } from './types'

/**
 * 새 ICS URL 구독을 추가한다.
 */
export const subscribe = async (
  url: string,
  displayName: string,
  color: string,
  refreshInterval: RefreshInterval = 'no',
  callback: (success: boolean) => void = () => {},
): Promise<boolean> => {
  const id = crypto.randomUUID()

  const subscription: ICSSubscription = {
    id,
    url,
    displayName,
    color,
    refreshInterval,
    lastSynced: null,
    isSync: true,
    timezone: null,
  }

  // settings에 저장
  const subscriptions = _loadSubscriptions()
  subscriptions.push(subscription)
  _saveSubscriptions(subscriptions)

  // AppCalendar 생성 및 등록
  const calendar = _createAppCalendar(subscription)
  _upsertCalendar(calendar)
  folderEmitter.emit('folder')

  // ICS 다운로드 및 파싱
  const fetchResult = await _fetchAndParseICS(subscription)
  if (fetchResult) {
    const { events: eventDict, calendarName, timezone } = fetchResult
    calendar.events = eventDict

    // ICS에서 추출한 이름으로 displayName 덮어쓰기 (사용자가 명시적으로 지정하지 않은 경우)
    if (calendarName && (!displayName || displayName === url)) {
      subscription.displayName = calendarName
      calendar.displayName = calendarName
    }

    // timezone 저장
    if (timezone) {
      subscription.timezone = timezone
    }

    // classInfo 업데이트
    _upsertCalendar(calendar)

    // 인스턴스 생성
    classInfo.instances = _createInstances(eventDict, false, classInfo.instances)
    instanceEmitter.emit('instance')

    // lastSynced 업데이트 (subscriptions 배열에 이미 포함된 참조를 직접 갱신)
    subscription.lastSynced = new Date().toISOString()
    _saveSubscriptions(subscriptions)

    folderEmitter.emit('folder')
    _setupTimer(subscription)
    _pushToServer(subscriptions)
    callback(true)
    return true
  } else {
    // fetch 실패 시 rollback: settings와 classInfo에서 제거
    const rollbackSubs = _loadSubscriptions().filter((s) => s.id !== id)
    _saveSubscriptions(rollbackSubs)
    _removeCalendar(id)
    folderEmitter.emit('folder')
    callback(false)
    return false
  }
}

/**
 * 구독을 해제하고 관련 데이터를 모두 제거한다.
 */
export const unsubscribe = (
  subscriptionId: string,
  callback: (success: boolean) => void = () => {},
): void => {
  _clearTimer(subscriptionId)
  refreshLocks.delete(subscriptionId)
  _removeCalendar(subscriptionId)

  const subscriptions = _loadSubscriptions().filter((s) => s.id !== subscriptionId)
  _saveSubscriptions(subscriptions)

  // 캐시 삭제
  icsDB.deleteData(subscriptionId).catch(() => {})

  folderEmitter.emit('folder')
  instanceEmitter.emit('instance')
  _pushToServer(subscriptions)
  callback(true)
}

/**
 * 구독 정보를 업데이트한다 (이름, 색상, URL, refreshInterval).
 */
export const updateSubscription = async (
  subscriptionId: string,
  updates: Partial<
    Pick<ICSSubscription, 'displayName' | 'color' | 'url' | 'refreshInterval' | 'isSync'>
  >,
  callback: (success: boolean) => void = () => {},
): Promise<void> => {
  const subscriptions = _loadSubscriptions()
  const idx = subscriptions.findIndex((s) => s.id === subscriptionId)
  if (idx < 0) {
    callback(false)
    return
  }

  const oldSubscription = subscriptions[idx]
  const urlChanged = updates.url !== undefined && updates.url !== oldSubscription.url
  const subscription = { ...oldSubscription, ...updates }
  subscriptions[idx] = subscription
  _saveSubscriptions(subscriptions)

  // 캘린더 메타 업데이트
  const existingCalendar = _findCalendar(subscriptionId)
  const existingEvents = existingCalendar?.events || {}
  const calendar = _createAppCalendar(subscription, existingEvents)

  if (updates.color) {
    // 이벤트 색상 업데이트
    const newColor = updates.color
    const oldColor = oldSubscription.color
    Object.values(calendar.events).forEach((e) => {
      e.color = newColor
    })

    // 기존 인스턴스 color 업데이트
    const calendarUrl = _makeCalendarUrl(subscriptionId)
    classInfo.instances.forEach((instance) => {
      if (instance.serverId.startsWith(calendarUrl)) {
        instance.changeFolderColor(newColor)
        if (instance.color === oldColor) {
          ;(instance as any).color = newColor
        }
      }
    })
  }

  _upsertCalendar(calendar)
  folderEmitter.emit('folder')

  // color 변경 시 instance 리렌더 트리거
  if (updates.color) {
    instanceEmitter.emit('instance')
  }

  // 타이머 상태를 먼저 재설정 (구 타이머가 refresh 중 fire하는 것 방지)
  if (updates.isSync !== undefined || updates.refreshInterval !== undefined || urlChanged) {
    const needsTimer = subscription.isSync && REFRESH_INTERVALS[subscription.refreshInterval] > 0
    if (needsTimer) {
      _setupTimer(subscription)
    } else {
      _clearTimer(subscriptionId)
    }
  }

  // URL 변경 시 force=true (새 URL이므로 캐시 무효), isSync 전환 시 캐시 검사
  if (urlChanged || (updates.isSync === true && !oldSubscription.isSync)) {
    // isSync 재활성화 시: 캐시에서 이벤트를 즉시 복원하여 현재 뷰를 갱신한다.
    // refreshSubscription은 캐시가 유효하면 네트워크 요청을 스킵하므로,
    // 캐시 복원을 먼저 수행해야 UI가 즉시 반영된다.
    if (updates.isSync === true && !oldSubscription.isSync && !urlChanged) {
      const cached = await _loadCachedICS(subscription)
      if (cached) {
        _replaceCalendarEvents(calendar, cached.events)
      }
    }
    await refreshSubscription(subscriptionId, () => {}, { force: urlChanged })
  }

  _pushToServer(subscriptions)
  callback(true)
}

/**
 * 단일 구독의 ICS 데이터를 다시 다운로드하고 파싱하는 내부 구현.
 * force가 false이면 캐시 유효성을 검사하여 불필요한 다운로드를 방지한다.
 */
const _doRefresh = async (
  subscriptionId: string,
  callback: (success: boolean) => void,
  options?: { force?: boolean },
): Promise<void> => {
  const subscriptions = _loadSubscriptions()
  const subscription = subscriptions.find((s) => s.id === subscriptionId)
  if (!subscription) {
    callback(false)
    return
  }

  const calendar = _findCalendar(subscriptionId)
  if (!calendar) {
    callback(false)
    return
  }

  // settings.setValue는 Redux Saga를 통해 비동기로 갱신되므로
  // _loadSubscriptions()가 stale 값을 반환할 수 있다.
  // classInfo의 calendar가 동기적 source of truth이므로 isSync를 동기화한다.
  if (subscription.isSync !== calendar.isSync) {
    subscription.isSync = calendar.isSync
  }

  // 캐시 유효성 검사: force가 아닌 경우 캐시가 유효하면 다운로드 스킵
  if (!options?.force) {
    const needsRefresh = await _shouldRefresh(subscription)
    if (!needsRefresh) {
      callback(true)
      return
    }
  }

  // ICS 다운로드 및 파싱
  const fetchResult = await _fetchAndParseICS(subscription)
  if (fetchResult) {
    const { events: eventDict } = fetchResult
    _replaceCalendarEvents(calendar, eventDict)

    // lastSynced 업데이트: 최신 settings를 다시 읽어 stale 덮어쓰기 방지
    const freshSubs = _loadSubscriptions()
    const freshSub = freshSubs.find((s) => s.id === subscriptionId)
    if (freshSub) {
      freshSub.lastSynced = new Date().toISOString()
      // classInfo에서 최신 isSync를 동기화
      freshSub.isSync = calendar.isSync
      _saveSubscriptions(freshSubs)
    }

    callback(true)
  } else {
    callback(false)
  }
}

/**
 * 단일 구독의 ICS 데이터를 다시 다운로드하고 파싱한다.
 * 동일 구독에 대한 동시 실행을 Promise 체이닝으로 직렬화한다.
 *
 * @param options.force - true이면 캐시 유효성 검사를 건너뛰고 항상 다운로드한다.
 */
export const refreshSubscription = async (
  subscriptionId: string,
  callback: (success: boolean) => void = () => {},
  options?: { force?: boolean },
): Promise<void> => {
  const tail = (refreshLocks.get(subscriptionId) ?? Promise.resolve()).then(() =>
    _doRefresh(subscriptionId, callback, options),
  )
  refreshLocks.set(
    subscriptionId,
    tail.then(
      () => {},
      () => {},
    ),
  )
  return tail
}

// Late-bound 바인딩: timerManager와 serverSync에 refreshSubscription 참조 전달
_bindRefreshFn(refreshSubscription)
_bindServerSyncRefreshFn(refreshSubscription)

/**
 * 캐시 유효성을 확인하여 갱신이 필요한 구독만 다운로드한다.
 * initSubscriptions 전용. 수동 갱신/타이머는 기존 refreshSubscription 사용.
 */
const refreshSubscriptionsIfNeeded = async (): Promise<void> => {
  const subscriptions = _loadSubscriptions()
  const activeSubscriptions = subscriptions.filter((s) => s.isSync)

  const needsRefresh = await Promise.all(
    activeSubscriptions.map(async (sub) => ({
      sub,
      needed: await _shouldRefresh(sub),
    })),
  )

  await Promise.all(
    needsRefresh.filter(({ needed }) => needed).map(({ sub }) => refreshSubscription(sub.id)),
  )
}

/**
 * 캐시 기반으로 구독 인스턴스를 재생성한다 (네트워크 없이).
 * 월 네비게이션 등으로 currentDate가 변경되었을 때,
 * RRULE 이벤트의 인스턴스를 새 날짜 범위로 다시 생성한다.
 *
 * 빠른 연속 호출 시 직렬화하여 중복 인스턴스를 방지한다.
 */
export const reloadSubscriptionInstances = (): Promise<void> => {
  const task = _reloadLock.then(() => _doReloadSubscriptionInstances())
  _setReloadLock(
    task.then(
      () => {},
      () => {},
    ),
  )
  return task
}

const _doReloadSubscriptionInstances = async (): Promise<void> => {
  const subscriptions = _loadSubscriptions()
  const activeSubs = subscriptions.filter((s) => s.isSync)

  let hasUpdated = false

  for (const sub of activeSubs) {
    const calendar = _findCalendar(sub.id)
    if (!calendar) continue

    const cached = await _loadCachedICS(sub)
    if (cached) {
      _replaceCalendarEvents(calendar, cached.events, { skipEmit: true })
    } else {
      // 캐시가 없으면 기존 events의 dirty 플래그를 세워 재생성
      _removeInstances(calendar.events)
      Object.values(calendar.events).forEach((e) => {
        e.dirty = true
      })
      classInfo.instances = _createInstances(calendar.events, true, classInfo.instances)
    }
    hasUpdated = true
  }

  if (hasUpdated) {
    instanceEmitter.emit('instance')
    folderEmitter.emit('folder')
  }
}

/**
 * 모든 활성 구독의 ICS 데이터를 갱신한다.
 */
export const refreshAllSubscriptions = async (): Promise<void> => {
  const subscriptions = _loadSubscriptions()
  const activeSubscriptions = subscriptions.filter((s) => s.isSync)

  await Promise.all(
    activeSubscriptions.map((s) => refreshSubscription(s.id, () => {}, { force: true })),
  )
}

/**
 * 현재 구독 목록을 반환한다.
 */
export const getSubscriptions = (): ICSSubscription[] => {
  return _loadSubscriptions()
}

/**
 * 앱 시작 시 구독을 복원하고 auto-refresh 타이머를 시작한다.
 * 서버 동기화를 수행하여 다른 디바이스의 변경사항을 반영한다.
 */
export const initSubscriptions = async (): Promise<void> => {
  const subscriptions = _loadSubscriptions()

  if (subscriptions.length === 0) {
    _registerSettingListener()
    try {
      await settingSyncService.sync()
    } catch (err) {
      console.error('Initial subscription sync failed (empty path):', err)
    }
    return
  }

  // 1. AppCalendar 복원 + 타이머 설정
  for (const sub of subscriptions) {
    _upsertCalendar(_createAppCalendar(sub))
    _setupTimer(sub)
  }
  folderEmitter.emit('folder')
  _registerSettingListener()

  // 2. 서버 sync (force=true: 앱 시작 시 throttle 우회)
  try {
    await settingSyncService.sync()
  } catch (err) {
    console.error('Initial subscription sync failed:', err)
    return
  }

  // 3. 캐시 로드
  const activeSubs = _loadSubscriptions().filter((s) => s.isSync)
  let hasCacheLoaded = false
  for (const sub of activeSubs) {
    const cached = await _loadCachedICS(sub)
    if (!cached) continue
    const calendar = _findCalendar(sub.id)
    if (!calendar) continue
    _replaceCalendarEvents(calendar, cached.events, { skipEmit: true })
    hasCacheLoaded = true
  }
  if (hasCacheLoaded) {
    instanceEmitter.emit('instance')
    folderEmitter.emit('folder')
  }

  // 4. 필요 시 네트워크 refresh
  await refreshSubscriptionsIfNeeded()
}

/**
 * 모든 auto-refresh 타이머를 정리한다 (로그아웃 시 호출).
 */
export const stopAutoRefresh = (): void => {
  clearAllTimers()
  resetSyncState()
}

// Re-export for backward compatibility
export { applyServerSubscriptions } from './serverSync'
